import "@/App.css";
import AdminShell from "@/components/admin/AdminShell";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { I18nProvider } from "@/lib/i18n";
import { AppReCaptchaProvider } from "@/lib/recaptcha";
import RouteSeo from "@/components/RouteSeo";
import Landing from "@/pages/Landing";
import AdminLogin from "@/pages/AdminLogin";
import {
  AdminForgotPassword,
  AdminResetPassword,
  AdminChangePassword,
} from "@/pages/AdminPasswordReset";
import AdminLeads from "@/pages/AdminLeads";
import AdminApplications from "@/pages/AdminApplications";
import AdminGrievances from "@/pages/AdminGrievances";
import AdminOffers from "@/pages/AdminOffers";
import AdminBlogs from "@/pages/AdminBlogs";
import AdminOnboardingLog from "@/pages/AdminOnboardingLog";
import AdminOnboardingBoard from "@/pages/AdminOnboardingBoard";
import AdminInvitations from "@/pages/AdminInvitations";
import ApplyForm from "@/pages/ApplyForm";
import PolicyHub from "@/pages/PolicyHub";
import Grievance from "@/pages/Grievance";
import CustomerLetterPortal from "@/pages/CustomerLetterPortal";
import TrackStatus from "@/pages/TrackStatus";
import BureauConsentPage from "@/pages/BureauConsent";
import AdminIntegrationsPage from "@/pages/AdminIntegrations";
import Training from "@/pages/Training";
import Governance from "@/pages/Governance";
import Opportunity from "@/pages/Opportunity";
// Note: legacy Careers page replaced by Opportunity (poster-driven career hub).
import HotOffers from "@/pages/HotOffers";
import Blogs, { BlogDetail } from "@/pages/Blogs";
import Investors from "@/pages/Investors";
import CustomerServices from "@/pages/CustomerServices";
import CSR from "@/pages/CSR";
import LoanRates from "@/pages/LoanRates";
import VisionMission from "@/pages/VisionMission";
import PartnerDashboard, { PartnerLogin } from "@/pages/PartnerPortal";
import { PartnerForgotPassword, PartnerResetPassword } from "@/pages/PartnerPasswordReset";
import { InvestorLogin, InvestorDashboard } from "@/pages/InvestorPortal";
import { InvestorForgotPassword, InvestorResetPassword } from "@/pages/InvestorPasswordReset";
import AdminInvestorsPage from "@/pages/AdminInvestors";
import AdminManualPayments from "@/pages/AdminManualPayments";
import AdminEligibilityPage from "@/pages/AdminEligibility";
import AdminEligibilityV2Preview from "@/pages/AdminEligibilityV2Preview";
import AdminCreditPoliciesPage from "@/pages/AdminCreditPolicies";
import AdminLetterQAPage from "@/pages/AdminLetterQA";
import AdminSmsLog from "@/pages/AdminSmsLog";
import AdminLettersIssued from "@/pages/AdminLettersIssued";
import AdminAiHelper from "@/pages/AdminAiHelper";
import { PublicChatWidget } from "@/components/AiChatPanel";
import { AdminAiChipGate } from "@/components/AdminAiChip";
import { useLocation } from "react-router-dom";

/**
 * Renders the public "Chat with us" widget on marketing/customer routes only.
 * Hides on any /admin, /partner/dashboard, /investor/dashboard where the user
 * is a staff/partner/investor (they have the AI Helper or their own portal).
 */
function PublicChatWidgetGate() {
  const { pathname } = useLocation();
  const HIDE_PREFIXES = ["/admin", "/partner/dashboard", "/investor/dashboard"];
  if (HIDE_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  return <PublicChatWidget />;
}
import AdminPartnersPage from "@/pages/AdminPartners";
import AdminRegistrations from "@/pages/AdminRegistrations";
import AdminSecurity from "@/pages/AdminSecurity";
import AdminStaffPage from "@/pages/AdminStaff";
import CrmDashboard from "@/pages/CrmDashboard";
import KanbanPipeline from "@/pages/KanbanPipeline";
import Customer360Page from "@/pages/Customer360";
import EligibilityCheck, { EligibilityResult } from "@/pages/EligibilityCheck";
import ReferralCalculator from "@/pages/ReferralCalculator";
import LocalLandingPage from "@/pages/LocalLandingPage";
import About from "@/pages/About";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsAndConditions from "@/pages/TermsAndConditions";
import RefundPolicy from "@/pages/RefundPolicy";
import Leadership from "@/pages/Leadership";

function App() {
  return (
    <div className="App">
      {/* Skip-to-content link — visible only when tab-focused (WCAG 2.4.1) */}
      <a href="#main-content" className="skip-link" data-testid="skip-to-main">
        Skip to main content
      </a>
      <HelmetProvider>
      <I18nProvider>
        <AppReCaptchaProvider>
        <BrowserRouter>
        <RouteSeo />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/about" element={<About />} />
          <Route path="/apply" element={<Navigate to="/apply/loan" replace />} />
          <Route path="/apply/:type" element={<ApplyForm />} />
          <Route path="/policies" element={<PolicyHub />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/leadership" element={<Leadership />} />
          <Route path="/grievance" element={<Grievance />} />
          <Route path="/training" element={<Training />} />
          <Route path="/governance" element={<Governance />} />
          <Route path="/careers" element={<Opportunity />} />
          <Route path="/hot-offers" element={<HotOffers />} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/blogs/:slug" element={<BlogDetail />} />
          <Route path="/investors" element={<Investors />} />
          <Route path="/customer-services" element={<CustomerServices />} />
          <Route path="/csr" element={<CSR />} />
          <Route path="/opportunity" element={<Navigate to="/careers" replace />} />
          <Route path="/loan-rates" element={<LoanRates />} />
          <Route path="/loan/:combo" element={<LocalLandingPage />} />
          <Route path="/vision" element={<VisionMission />} />
          <Route path="/mission" element={<Navigate to="/vision" replace />} />
          <Route path="/partner/login" element={<PartnerLogin />} />
          <Route path="/partner/forgot-password" element={<PartnerForgotPassword />} />
          <Route path="/partner/reset-password" element={<PartnerResetPassword />} />
          <Route path="/partner/dashboard" element={<PartnerDashboard />} />
          <Route path="/partner" element={<Navigate to="/partner/login" replace />} />
          <Route path="/partnership" element={<Navigate to="/careers" replace />} />
          <Route path="/investor/login" element={<InvestorLogin />} />
          <Route path="/investor/forgot-password" element={<InvestorForgotPassword />} />
          <Route path="/investor/reset-password" element={<InvestorResetPassword />} />
          <Route path="/investor/dashboard" element={<InvestorDashboard />} />
          <Route path="/investor" element={<Navigate to="/investor/login" replace />} />
          <Route path="/eligibility" element={<EligibilityCheck />} />
          <Route path="/eligibility/result" element={<EligibilityResult />} />
          <Route path="/my-letter" element={<CustomerLetterPortal />} />
          <Route path="/track" element={<TrackStatus />} />
          <Route path="/bureau-consent/:token" element={<BureauConsentPage />} />
          <Route path="/referral-calculator" element={<ReferralCalculator />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
          <Route path="/admin/reset-password" element={<AdminResetPassword />} />
          <Route element={<AdminShell />}>
            <Route path="/admin/change-password" element={<AdminChangePassword />} />
            <Route path="/admin/leads" element={<AdminLeads />} />
            <Route path="/admin/applications" element={<AdminApplications />} />
            <Route path="/admin/grievances" element={<AdminGrievances />} />
            <Route path="/admin/offers" element={<AdminOffers />} />
            <Route path="/admin/blogs" element={<AdminBlogs />} />
            <Route path="/admin/onboarding-log" element={<AdminOnboardingLog />} />
            <Route path="/admin/onboarding" element={<AdminOnboardingBoard />} />
            <Route path="/admin/invitations" element={<AdminInvitations />} />
            <Route path="/admin/investors" element={<AdminInvestorsPage />} />
            <Route path="/admin/manual-payments" element={<AdminManualPayments />} />
            <Route path="/admin/eligibility-checks" element={<AdminEligibilityPage />} />
            <Route path="/admin/eligibility-v2/preview/:checkId" element={<AdminEligibilityV2Preview />} />
            <Route path="/admin/credit-policies" element={<AdminCreditPoliciesPage />} />
            <Route path="/admin/letter-qa" element={<AdminLetterQAPage />} />
            <Route path="/admin/integrations" element={<AdminIntegrationsPage />} />
            <Route path="/admin/sms/log" element={<AdminSmsLog />} />
            <Route path="/admin/letters-issued" element={<AdminLettersIssued />} />
            <Route path="/admin/ai-helper" element={<AdminAiHelper />} />
            <Route path="/admin/partners" element={<AdminPartnersPage />} />
            <Route path="/admin/registrations" element={<AdminRegistrations />} />
            <Route path="/admin/security" element={<AdminSecurity />} />
            <Route path="/admin/staff" element={<AdminStaffPage />} />
            <Route path="/admin/dashboard" element={<CrmDashboard />} />
            <Route path="/admin/pipeline" element={<KanbanPipeline />} />
            <Route path="/admin/customer" element={<Customer360Page />} />
            <Route path="/admin/customer/:phone" element={<Customer360Page />} />
          </Route>
        </Routes>
        {/* Public floating "Chat with us" widget — only mounts on public
            marketing / customer routes. Hidden on admin, partner, investor
            portals where staff have the AI Helper instead. */}
        <PublicChatWidgetGate />
        {/* Floating AI Helper chip on every authenticated admin page. Hidden
            on the standalone AI Helper page and the login page. */}
        <AdminAiChipGate />
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton toastOptions={{ style: { background: "#0A1F44", color: "#fff", border: "1px solid rgba(212,175,55,.45)" } }} />
      </AppReCaptchaProvider>
      </I18nProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
